import React, { useEffect, useRef, useState, useCallback } from "react";
import { useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import {
  type SavedMapPlotRequestDetail,
  type PropertySelectionRow,
} from "../../types";
import { type SavedMapPlotDataWithRequestId } from "../../types/component";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { type AxiosInstance } from "axios";
import { toast } from "react-toastify";
import { ThemeProvider } from "@mui/material";
import { theme } from "@/types/theme";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { SavedPlotPopupContent } from "../SavedPlotPopupContent";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { setActivePopup } from "../../utils/tagSync/activePopupManager";
import { useAdditionalFieldsConfig } from "../../hooks/useAdditionalFieldsConfig";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { convertSavedPlotToPropertyRow } from "../../utils/savedPlot/convertSavedPlotToPropertyRow";
import type { AddressIntegrator } from "@/utils/AddressIntegrator";
import type { ChibanInput } from "@/types/kaokuNumber";
import { CanvasPinMarker } from "../../utils/canvasPin/CanvasPinMarker";
import { type CanvasPinRenderer } from "../../utils/canvasPin/CanvasPinRenderer";
import { getSharedCanvasPinRenderer } from "../../utils/canvasPin/sharedCanvasPinRenderer";
import {
  PIN_COLORS,
  getTeardropPinIcon,
} from "../../utils/canvasPin/teardropPin";
import { type CanvasPinIcon } from "../../utils/canvasPin/CanvasPinIcon";

interface SavedPlotLayerProps {
  data: SavedMapPlotRequestDetail[] | null | undefined;
  /** 物件リストID → ピンの色。複数のリストを出した時にリストごとに色を分ける。無いリストは緑 */
  colorByRequestId?: ReadonlyMap<number, string>;
  defaultVisible: boolean;
  savedMapPlotData?: SavedMapPlotDataWithRequestId | null;
  setSelectedProperties?: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  setCurrentPopupRowId?: React.Dispatch<React.SetStateAction<number | null>>;
  visible?: boolean;
  // 地番検索
  kaokuNumberSearchFlag?: boolean;
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void;
  // 役所資料取得依頼
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: (
    prefecture: string,
    address: string,
    chiban: string,
    location: string,
    fromPopup?: boolean
  ) => void;
  // 物件概要書
  propertySummary?: boolean;
  handleNewPropertySheet?: (adi: AddressIntegrator) => void;
}

export const SAVED_PLOT_LAYER_NAME = "保存されたプロット情報を表示する";

export const SavedPlotLayer: React.FC<SavedPlotLayerProps> = ({
  data,
  colorByRequestId,
  defaultVisible,
  savedMapPlotData,
  setSelectedProperties,
  setCurrentPopupRowId,
  visible: visibleProp,
  // 地番検索
  kaokuNumberSearchFlag = false,
  handleKaokuNumberSearch,
  // 役所資料取得依頼
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal,
  // 物件概要書
  propertySummary = false,
  handleNewPropertySheet,
}) => {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup>(L.layerGroup());
  const [visible, setVisible] = useState(defaultVisible);
  const { apiClient } = useApiClient();
  const { crm: crmFeatureFlag, priceMap: priceMapFeatureFlag } =
    useFeatureFlags();
  const crm = Boolean(crmFeatureFlag);
  const priceMap = Boolean(priceMapFeatureFlag);
  const firstMarkerRef = useRef<CanvasPinMarker | null>(null);
  const markersMapRef = useRef<Map<number, CanvasPinMarker>>(new Map());
  // 各マーカーをいま描いている色（リストの色が変わった時だけ描き直すため）
  const markerColorsRef = useRef<Map<number, string>>(new Map());
  // 各プロットの最新の備考を保持するMap
  const remarksMapRef = useRef<Map<number, string>>(new Map());
  // 各プロットの最新のタグを保持するMap
  const tagsMapRef = useRef<Map<number, string[]>>(new Map());
  // 各プロットの最新の追加項目を保持するMap
  const additionalFieldsMapRef = useRef<Map<number, string>>(new Map());
  // 追加項目設定を取得
  const { availableFields } = useAdditionalFieldsConfig();

  // マーカークリック時にPropertySelectionGridに追加する関数
  const handleMarkerClick = useCallback(
    (plot: SavedMapPlotRequestDetail): void => {
      if (!crm || !setSelectedProperties) {
        return;
      }

      setSelectedProperties((prevProperties) => {
        // Math.maxのスプレッド演算子は引数が多すぎるとエラーになるためreduceで処理
        const nextId =
          prevProperties.reduce((maxId, p) => Math.max(p.id, maxId), 0) + 1;

        // 変換関数を使用してPropertySelectionRowに変換
        const newRow = convertSavedPlotToPropertyRow(plot, nextId);

        // バリデーション失敗時（所在・地番がない）はリスト追加しない
        if (!newRow) {
          return prevProperties;
        }

        // 重複チェック（種別・所在・地番・都道府県の組み合わせで判定）
        const existingRow = prevProperties.find(
          (p) =>
            p.bookType === newRow.bookType &&
            p.prefName === newRow.prefName &&
            p.location === newRow.location &&
            p.chibanKaokuNumber === newRow.chibanKaokuNumber
        );

        if (existingRow) {
          // 既に存在する場合は選択状態にする
          if (setCurrentPopupRowId) {
            setCurrentPopupRowId(existingRow.id);
          }
          return prevProperties;
        }

        // 新規追加
        if (setCurrentPopupRowId) {
          setCurrentPopupRowId(nextId);
        }

        return [...prevProperties, newRow];
      });
    },
    [crm, setSelectedProperties, setCurrentPopupRowId]
  );

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: L.LayersControlEvent): void => {
      if (e.name === SAVED_PLOT_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: L.LayersControlEvent): void => {
      if (e.name === SAVED_PLOT_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  // 備考保存時のコールバック
  const handleRemarksSaved = (plotId: number, newRemarks: string): void => {
    remarksMapRef.current.set(plotId, newRemarks);
  };

  // タグ保存時のコールバック
  const handleTagsSaved = (plotId: number, newTags: string[]): void => {
    tagsMapRef.current.set(plotId, newTags);
  };

  // 追加項目保存時のコールバック
  const handleAdditionalFieldsSaved = (
    plotId: number,
    newAdditionalFields: string
  ): void => {
    additionalFieldsMapRef.current.set(plotId, newAdditionalFields);
  };

  // OwnerPlotLayerパターンに統一: マーカーをキャッシュし、データが変わった時だけ再作成
  useEffect(() => {
    if (!map || !visible || !data) return;

    // 土地のみをフィルタリング（経緯:https://trustart-group.slack.com/archives/C04HA6M7SMB/p1763444573320059?thread_ts=1763442229.347609&cid=C04HA6M7SMB）
    // canvas は追加した順に重ねて描くため、L.Marker と同じく南のピンが上に来るよう北から並べる
    const onlyLandPlots = data
      .filter((plot) => plot.realEstateType === "土地")
      .sort(
        (a, b) =>
          (b.substituteLatitude ?? -Infinity) -
          (a.substituteLatitude ?? -Infinity)
      );

    // 現在のデータに基づくプロットIDのセット（削除対象の特定に使用）
    const currentPlotIds = new Set<number>();
    let newMarkerCount = 0;

    onlyLandPlots.forEach((plot) => {
      currentPlotIds.add(plot.id);
      const color =
        colorByRequestId?.get(plot.mapPlotRequestId) ?? PIN_COLORS.savedPlot;

      // 既存のマーカーがキャッシュにあるかチェック
      // キャッシュがあれば再利用（ポップアップが閉じる問題を防ぐ）
      const existingMarker = markersMapRef.current.get(plot.id);

      if (existingMarker) {
        // 出し直しでリストの順番が変わった時は、色だけ塗り直す
        if (markerColorsRef.current.get(plot.id) !== color) {
          const { icon, selectedIcon } = getSavedPlotPinIcons(plot, color, crm);
          existingMarker.setIcon(icon).setSelectedIcon(selectedIcon);
          markerColorsRef.current.set(plot.id, color);
        }
      } else {
        // 新しいマーカーを作成
        const newMarker = createSavedPlotMarker(
          plot,
          color,
          // ピンは1枚の canvas にまとめて描く（DOM のマーカーだと数千件で地図が固まるため）
          getSharedCanvasPinRenderer(map),
          apiClient,
          map,
          savedMapPlotData?.plotRequestId,
          handleRemarksSaved,
          handleTagsSaved,
          handleAdditionalFieldsSaved,
          remarksMapRef.current,
          tagsMapRef.current,
          additionalFieldsMapRef.current,
          availableFields,
          crm,
          priceMap,
          handleMarkerClick,
          kaokuNumberSearchFlag,
          handleKaokuNumberSearch,
          realEstateDocumentFetchFlag,
          handleRealEstateDocumentFetchModal,
          propertySummary,
          handleNewPropertySheet
        );

        if (newMarker) {
          addPopupSyncEvents(newMarker, plot);

          // マーカーをキャッシュとレイヤーに追加
          markersMapRef.current.set(plot.id, newMarker);
          markerColorsRef.current.set(plot.id, color);
          layerRef.current.addLayer(newMarker);

          // 最初のマーカーとして記録
          if (newMarkerCount === 0) {
            firstMarkerRef.current = newMarker;
          }
          newMarkerCount += 1;
        }
      }
    });

    // データから削除されたプロットのマーカーをクリーンアップ
    // （リストを地図から外して data が空になった時も、ここで残りのピンを消す）
    cleanupObsoleteMarkers(
      markersMapRef.current,
      currentPlotIds,
      layerRef.current
    );
    markerColorsRef.current.forEach((_, plotId) => {
      if (!currentPlotIds.has(plotId)) markerColorsRef.current.delete(plotId);
    });

    // 新しいマーカーが追加された場合のみ地図移動とトースト表示
    if (newMarkerCount > 0 && firstMarkerRef.current) {
      const position = firstMarkerRef.current.getLatLng();
      map.setView(position, 17);

      // リストは足していくので、件数は今回足したピンの数
      const markerCount = newMarkerCount;
      toast.success(
        crm
          ? `物件リストを地図に反映しました（${formatNumberWithComma(
              markerCount
            )}件）`
          : `インポートしたピンをプロットしました（${formatNumberWithComma(
              markerCount
            )}件）`
      );
    }
  }, [map, visible, data, colorByRequestId, apiClient, savedMapPlotData, crm]);

  useEffect(() => {
    if (layerRef.current) {
      if (visible) {
        layerRef.current.addTo(map);
      } else {
        map.removeLayer(layerRef.current);
      }
    }
  }, [visible, map]);

  return <FeatureGroup />;
};

/**
 * マーカーキャッシュから不要なマーカーを削除する
 * 現在のデータに存在しないマーカーをレイヤーとキャッシュから削除
 * @param markersCache マーカーキャッシュ
 * @param currentPlotIds 現在存在するプロットIDのセット
 * @param layer マーカーが追加されているレイヤー
 */
const cleanupObsoleteMarkers = (
  markersCache: Map<number, CanvasPinMarker>,
  currentPlotIds: Set<number>,
  layer: L.LayerGroup | null
): void => {
  markersCache.forEach((marker, plotId) => {
    if (!currentPlotIds.has(plotId)) {
      layer?.removeLayer(marker);
      markersCache.delete(plotId);
    }
  });
};

/**
 * マーカーにポップアップイベントを追加する
 * ポップアップが開かれた際にアクティブなポップアップとして登録
 */
const addPopupSyncEvents = (
  marker: CanvasPinMarker,
  plot: SavedMapPlotRequestDetail
): void => {
  marker.on("popupopen", () => {
    // アクティブなポップアップとして登録
    const bookType = plot.realEstateType ?? "土地";
    const chibanOrKaokuNumber =
      bookType === "土地" ? plot.requestChiban : plot.requestKaokuNumber;

    if (plot.requestLocation && chibanOrKaokuNumber) {
      const normalizedLocation = NYHalfWidthNumberToFillWidthNumber(
        plot.requestLocation
      );
      const normalizedChiban =
        NYHalfWidthNumberToFillWidthNumber(chibanOrKaokuNumber);
      setActivePopup({
        location: normalizedLocation,
        chiban: normalizedChiban,
        prefName: plot.prefName ?? "",
        pinType: "saved-plot",
      });
    }
  });

  // ポップアップが閉じられたときにアクティブポップアップをクリア
  marker.on("popupclose", () => {
    setActivePopup(null);
  });
};

/**
 * プロット情報のポップアップを作成する
 * 中身（React）は初めて開いた時に描画する。ピン生成時に全件分描画すると、
 * 件数が多い時にメモリと生成時間を大きく消費するため
 */
const createSavedPlotPopup = (
  plot: SavedMapPlotRequestDetail,
  apiClient: AxiosInstance,
  map: L.Map,
  plotRequestId: number | undefined,
  onRemarksSaved: (plotId: number, newRemarks: string) => void,
  onTagsSaved: (plotId: number, newTags: string[]) => void,
  onAdditionalFieldsSaved: (
    plotId: number,
    newAdditionalFields: string
  ) => void,
  remarksMap: Map<number, string>,
  tagsMap: Map<number, string[]>,
  additionalFieldsMap: Map<number, string>,
  availableFields: AdditionalFieldConfig | undefined,
  crm: boolean,
  // 地価マップ有効、無効の判定フラグ
  priceMap: boolean,
  kaokuNumberSearchFlag?: boolean,
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void,
  realEstateDocumentFetchFlag?: boolean,
  handleRealEstateDocumentFetchModal?: (
    prefecture: string,
    address: string,
    chiban: string,
    location: string,
    fromPopup?: boolean
  ) => void,
  propertySummary?: boolean,
  handleNewPropertySheet?: (adi: AddressIntegrator) => void
): L.Popup => {
  const popupHTML = document.createElement("div");

  // 地図コンテナの横幅
  const mapContainerSizeX = map.getSize().x;

  // ポップアップのデフォルトサイズ(px)
  let popupMaxWidth = 300;

  // 地図コンテナ幅375px以下では（300px幅のポップアップ＋余白）がきれいに収まらないので、ポップアップ幅を縮小する
  if (mapContainerSizeX < 375) {
    popupMaxWidth = Math.floor(mapContainerSizeX * 0.8);
  }

  const renderPopupContent = (): void => {
    // 最新の備考を取得（保存された備考があればそれを、なければ元のデータを使用）
    const currentRemarks = remarksMap.get(plot.id) ?? plot.remarks;

    // 最新のタグを取得（保存されたタグがあればそれを、なければ元のデータを使用）
    const savedTags = tagsMap.get(plot.id);
    const currentTags = savedTags
      ? savedTags.map((name) => ({ id: 0, name }))
      : plot.tags;

    // 最新の追加項目を取得（保存された追加項目があればそれを、なければ元のデータを使用）
    const savedAdditionalFieldsString = additionalFieldsMap.get(plot.id);
    const currentAdditionalFields: typeof plot.additionalFields =
      savedAdditionalFieldsString
        ? (JSON.parse(
            savedAdditionalFieldsString
          ) as typeof plot.additionalFields)
        : plot.additionalFields;

    const plotWithCurrentData = {
      ...plot,
      remarks: currentRemarks,
      tags: currentTags,
      additionalFields: currentAdditionalFields,
    };

    createRoot(popupHTML).render(
      <ThemeProvider theme={theme}>
        <SavedPlotPopupContent
          plot={plotWithCurrentData}
          apiClient={apiClient}
          map={map}
          plotRequestId={plot.mapPlotRequestId ?? plotRequestId}
          onRemarksSaved={onRemarksSaved}
          onTagsSaved={onTagsSaved}
          onAdditionalFieldsSaved={onAdditionalFieldsSaved}
          availableFields={availableFields}
          crm={crm}
          kaokuNumberSearchFlag={kaokuNumberSearchFlag}
          handleKaokuNumberSearch={handleKaokuNumberSearch}
          realEstateDocumentFetchFlag={realEstateDocumentFetchFlag}
          handleRealEstateDocumentFetchModal={
            handleRealEstateDocumentFetchModal
          }
          propertySummary={propertySummary}
          handleNewPropertySheet={handleNewPropertySheet}
          priceMap={priceMap}
        />
      </ThemeProvider>
    );
  };

  const popup = L.popup({
    content: popupHTML,
    maxWidth: popupMaxWidth,
  });

  // 一度描画したら閉じても root を残し、入力中の状態などを保つ（従来と同じ挙動）
  let rendered = false;
  popup.on("add", () => {
    if (rendered) return;
    rendered = true;
    // Leaflet は "add" の前に中身が空のままポップアップの幅・位置を決めてしまうため、
    // 同期的に描画してから測り直す
    flushSync(renderPopupContent);
    popup.update();
  });

  return popup;
};

/**
 * 保存されたプロットのピンの見た目（ふだん・選択中）を返す
 * @param color リストの色
 */
const getSavedPlotPinIcons = (
  plot: SavedMapPlotRequestDetail,
  color: string,
  crm: boolean
): { icon: CanvasPinIcon; selectedIcon: CanvasPinIcon } => {
  // 一覧の行にした時の形（所在・地番が無く一覧に入れられないものは null）
  const row = convertSavedPlotToPropertyRow(plot, 0);
  return {
    icon: getTeardropPinIcon(color),
    // CRM では開くと物件一覧に入るので、選択中は一覧の青で描く。
    // 所在・地番が無く一覧に入れられないものはリストの色のまま
    selectedIcon: getTeardropPinIcon(
      crm && row !== null ? PIN_COLORS.listed : color,
      { selected: true }
    ),
  };
};

/**
 * 保存されたプロットのマーカーを作成する
 */
const createSavedPlotMarker = (
  plot: SavedMapPlotRequestDetail,
  color: string,
  renderer: CanvasPinRenderer,
  apiClient: AxiosInstance,
  map: L.Map,
  plotRequestId: number | undefined,
  onRemarksSaved: (plotId: number, newRemarks: string) => void,
  onTagsSaved: (plotId: number, newTags: string[]) => void,
  onAdditionalFieldsSaved: (
    plotId: number,
    newAdditionalFields: string
  ) => void,
  remarksMap: Map<number, string>,
  tagsMap: Map<number, string[]>,
  additionalFieldsMap: Map<number, string>,
  availableFields: AdditionalFieldConfig | undefined,
  crm: boolean,
  // 地価マップ有効、無効の判定フラグ
  priceMap: boolean,
  onMarkerClick?: (plot: SavedMapPlotRequestDetail) => void,
  kaokuNumberSearchFlag?: boolean,
  handleKaokuNumberSearch?: (chibanList: ChibanInput[]) => void,
  realEstateDocumentFetchFlag?: boolean,
  handleRealEstateDocumentFetchModal?: (
    prefecture: string,
    address: string,
    chiban: string,
    location: string,
    fromPopup?: boolean
  ) => void,
  propertySummary?: boolean,
  handleNewPropertySheet?: (adi: AddressIntegrator) => void
): CanvasPinMarker | null => {
  if (plot.substituteLatitude === null || plot.substituteLongitude === null) {
    return null;
  }

  const popup = createSavedPlotPopup(
    plot,
    apiClient,
    map,
    plotRequestId,
    onRemarksSaved,
    onTagsSaved,
    onAdditionalFieldsSaved,
    remarksMap,
    tagsMap,
    additionalFieldsMap,
    availableFields,
    crm,
    priceMap,
    kaokuNumberSearchFlag,
    handleKaokuNumberSearch,
    realEstateDocumentFetchFlag,
    handleRealEstateDocumentFetchModal,
    propertySummary,
    handleNewPropertySheet
  );

  // 一覧の行にした時の形（所在・地番が無く一覧に入れられないものは null）
  const row = convertSavedPlotToPropertyRow(plot, 0);
  const marker = new CanvasPinMarker(
    [plot.substituteLatitude, plot.substituteLongitude],
    {
      ...getSavedPlotPinIcons(plot, color, crm),
      renderer,
      // 一覧の行と同じ物件として突き合わせる
      property: row
        ? {
            bookType: row.bookType,
            prefName: row.prefName,
            location: row.location,
            chibanKaokuNumber: row.chibanKaokuNumber,
          }
        : undefined,
    }
  ).bindPopup(popup);

  // マーカークリック時にPropertySelectionGridに追加
  if (onMarkerClick) {
    marker.on("click", () => {
      onMarkerClick(plot);
    });
  }

  return marker;
};
